import React from 'react';
import { NT, NI, NMButton } from './NaomiTheme';
import { MDialog, MTextField } from './NaomiUi';
import { evalRangeCheck } from './NaomiShared';
import { registerNaomiPlugins, registerLivePreviewExtra } from './NaomiRegistry';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { getCatalogueImages } from './services/CatalogueService';
import type { CatalogueImage } from './services/CatalogueService';
import { CataloguePicker } from './CataloguePicker';

export function ensureChoiceList(sdm: any, itemOid: string): any {
  const tv = sdm.TemplateVersion[0];
  const item = tv.ItemDef.find(i => i.OID === itemOid);
  if (!item) return sdm;
  if (!(item.DataType === 'singleselect' || item.DataType === 'multiselect')) return sdm;
  if (item.CodeListRef && tv.CodeList.find(c => c.OID === item.CodeListRef.CodeListOID)) return sdm;

  const used = new Set(tv.CodeList.map(c => c.OID));
  let n = tv.CodeList.length + 1;
  while (used.has(`CL.${n}`)) n++;
  const clOid = `CL.${n}`;
  const newCL = {
    OID: clOid, DataType: 'text', CodeListItem: [
      { CodedValue: '1', Decode: { TranslatedText: { lang: 'en', text: 'Option A' }}, IsEnabled: false },
      { CodedValue: '2', Decode: { TranslatedText: { lang: 'en', text: 'Option B' }}, IsEnabled: false },
      { CodedValue: '3', Decode: { TranslatedText: { lang: 'en', text: 'Option C' }}, IsEnabled: false },
    ]
  };
  return {
    ...sdm,
    TemplateVersion: sdm.TemplateVersion.map((v, i) => i === 0 ? {
      ...v,
      ItemDef: v.ItemDef.map(it => it.OID === itemOid ? { ...it, CodeListRef: { CodeListOID: clOid }} : it),
      CodeList: [...v.CodeList, newCL]
    } : v)
  };
}

export function evaluateValidation(value: any, validations: any, dataType: string): string | null {
  if (!validations) return null;
  const v = validations;
  if (v.required) {
    if (value === undefined || value === null || value === '' ||
        (Array.isArray(value) && !value.length)) {
      return v.requiredMessage || 'This field is required';
    }
  }
  if (value === undefined || value === null || value === '') return null;

  if (dataType === 'integer' || dataType === 'float' || dataType === 'currency' || dataType === 'percent' || dataType === 'feetinches') {
    const raw = (typeof value === 'object' && value !== null) ? (value as any).ft : value;
    const n = Number(raw);
    if (Number.isNaN(n)) return 'Enter a valid number';
    if (v._rangeChecks?.length) {
      for (const rc of v._rangeChecks) {
        const err = evalRangeCheck(rc, n);
        if (err) return err;
      }
    } else {
      if (v.min != null && n < Number(v.min)) return v.message || `Must be at least ${v.min}`;
      if (v.max != null && n > Number(v.max)) return v.message || `Must be at most ${v.max}`;
    }
  }
  if (dataType === 'text' || dataType === 'multilinetext') {
    const s = String(value);
    const minL = v.minLen ?? v.minLength;
    const maxL = v.maxLen ?? v.maxLength;
    if (minL != null && s.length < Number(minL)) return v.message || `Must be at least ${minL} characters`;
    if (maxL != null && s.length > Number(maxL)) return v.message || `Must be at most ${maxL} characters`;
    const patterns = {
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      phone: /^[\d\s().+-]{7,}$/,
      zip: /^\d{5}(-\d{4})?$/,
      url: /^https?:\/\/\S+$/i,
    };
    if (v.pattern && v.pattern !== 'custom' && patterns[v.pattern]) {
      if (!patterns[v.pattern].test(s)) return v.message || `Invalid ${v.pattern} format`;
    }
    if ((v.pattern === 'custom' && v.regex) || (!v.pattern && v.regex)) {
      try { if (!new RegExp(v.regex).test(s)) return v.message || 'Invalid format'; } catch {}
    }
  }
  if (dataType === 'multiselect') {
    const arr = Array.isArray(value) ? value : [];
    if (v.minSel != null && arr.length < Number(v.minSel)) return v.message || `Pick at least ${v.minSel}`;
    if (v.maxSel != null && arr.length > Number(v.maxSel)) return v.message || `Pick at most ${v.maxSel}`;
  }
  if (dataType === 'date' || dataType === 'datetime') {
    if (v.dateMin && String(value) < v.dateMin) return v.message || `Must be on or after ${v.dateMin}`;
    if (v.dateMax && String(value) > v.dateMax) return v.message || `Must be on or before ${v.dateMax}`;
  }
  return null;
}

export function renderGroupHeaderBanner(group: any): React.ReactNode | null {
  if (!group?.HeaderImage) return null;
  return (
    <div style={{ height: 90, width: '100%', borderRadius: 8, marginBottom: 8,
      background: `linear-gradient(rgba(0,0,0,.06), rgba(0,0,0,.18)), url("${group.HeaderImage}") center/cover no-repeat`,
      border: `1px solid ${NT.line}` }}/>
  );
}

export function GroupHeaderImageDialog({ open, onClose, group, onSave }: any): JSX.Element {
  const [url, setUrl] = React.useState(group?.HeaderImage || '');
  React.useEffect(() => { if (open) setUrl(group?.HeaderImage || ''); }, [open, group]);
  const samples = [
    'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=900&h=300&fit=crop',
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=900&h=300&fit=crop',
    'https://images.unsplash.com/photo-1572025442646-866d16c84a54?w=900&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581094271901-8022df4466f9?w=900&h=300&fit=crop',
  ];
  return (
    <MDialog id="naomi-preview-dialog-header-image" open={open} onClose={onClose} title="Section header image" icon={<NI.Eye/>} width={520}
      actions={<div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
        <NMButton id="naomi-preview-button-header-image-remove" variant="text" color="danger" disabled={!url} onClick={() => { onSave(null); onClose(); }}>
          Remove image
        </NMButton>
        <div style={{ display: 'flex', gap: 8 }}>
          <NMButton id="naomi-preview-button-header-image-cancel" variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>
          <NMButton id="naomi-preview-button-header-image-save" color="primary" onClick={() => { onSave(url || null); onClose(); }}>Save</NMButton>
        </div>
      </div>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <ImageDropPicker value={url} onChange={(u) => setUrl(u || '')}
          height={120} label="Drop image, click to browse, or paste"/>
        <MTextField id="naomi-preview-input-header-image-url" label="Or enter URL" value={url} onChange={setUrl}
          placeholder="https://…"/>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: NT.ink3, textTransform: 'uppercase',
            letterSpacing: .4, marginBottom: 6 }}>Sample stock photos</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {samples.map((s, idx) => (
              <button id={`naomi-preview-button-sample-image-${idx}`} key={s} onClick={() => setUrl(s)}
                style={{ height: 64, borderRadius: 4, cursor: 'pointer',
                  border: url === s ? `2px solid ${NT.primary}` : `1px solid ${NT.line}`,
                  background: `url("${s}") center/cover no-repeat`, padding: 0 }}/>
            ))}
          </div>
        </div>
      </div>
    </MDialog>
  );
}

export function ChoiceOptionImageInput({ value, imageUrl, thumbnailPreviewUrl, onChange }: any): JSX.Element {
  const { accessToken } = useOidcAccessToken();
  const [open, setOpen] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [imgError, setImgError] = React.useState(false);
  const [thumbError, setThumbError] = React.useState(false);
  const [freshPreviewUrl, setFreshPreviewUrl] = React.useState<string | null>(null);
  const [refreshing, setRefreshing] = React.useState(false);

  const hasImage = !!imageUrl;

  // When dialog opens with an image set, fetch a fresh SAS URL by matching the stored filename
  React.useEffect(() => {
    if (!open || !hasImage || !accessToken) return;
    setRefreshing(true);
    getCatalogueImages(accessToken)
      .then(images => {
        const match = images.find(img => img.imageUrl === imageUrl);
        if (match?.previewUrl) {
          setFreshPreviewUrl(match.previewUrl);
          setImgError(false);
        }
      })
      .catch(() => {})
      .finally(() => setRefreshing(false));
  }, [open, hasImage, imageUrl, accessToken]);

  // Reset thumb error when a new thumbnail URL arrives
  React.useEffect(() => { setThumbError(false); }, [thumbnailPreviewUrl]);

  const displaySrc = freshPreviewUrl || value || null;
  const thumbSrc = hasImage && !thumbError ? thumbnailPreviewUrl : null;

  return (
    <React.Fragment>
      <button id="naomi-preview-button-choice-option-image" onClick={e => { e.stopPropagation(); setOpen(true); }}
        title={hasImage ? 'Change image' : 'Add image to this option'}
        className="nm-icon-btn"
        style={{ width: 24, height: 24, padding: 0, overflow: 'hidden',
          background: thumbSrc ? 'transparent' : hasImage ? (NT as any).primarySoft ?? NT.surfaceAlt : NT.surfaceAlt,
          border: `1px solid ${hasImage ? NT.primary : NT.line2}`, borderRadius: 4,
          color: hasImage && !thumbSrc ? NT.primary : NT.ink3, cursor: 'pointer', flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {thumbSrc ? (
          <img src={thumbSrc} alt="" onError={() => setThumbError(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 3, display: 'block' }}/>
        ) : (
          <NI.Camera/>
        )}
      </button>
      {open && (
        <MDialog id="naomi-preview-dialog-choice-option-image" open={open} onClose={() => setOpen(false)} title="Option image" icon={<NI.Eye/>} width={420}
          actions={<div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', width: '100%' }}>
            <NMButton id="naomi-preview-button-choice-option-image-remove" variant="text" color="danger" disabled={!hasImage} onClick={() => { onChange(null); setOpen(false); }}>Remove</NMButton>
            <NMButton id="naomi-preview-button-choice-option-image-done" variant="text" color="neutral" onClick={() => setOpen(false)}>Done</NMButton>
          </div>}>
          {hasImage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10,
              fontSize: 12, color: NT.ink3, wordBreak: 'break-all' }}>
              <span style={{ color: NT.primary, display: 'inline-flex', flexShrink: 0 }}><NI.Camera/></span>
              <span style={{ color: NT.ink2, fontWeight: 500 }}>{imageUrl}</span>
            </div>
          )}
          <div onClick={() => setPickerOpen(true)}
            title={hasImage ? 'Click to change image' : 'Click to pick from catalogue'}
            style={{ height: 120, borderRadius: 6, cursor: 'pointer',
              background: NT.surfaceAlt,
              border: `1.5px dashed ${hasImage ? NT.primary : NT.line2}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: NT.ink4, flexDirection: 'column', gap: 6, overflow: 'hidden', position: 'relative' }}>
            {refreshing ? (
              <span style={{ fontSize: 12, opacity: 0.6 }}>Loading preview…</span>
            ) : hasImage && displaySrc && !imgError ? (
              <img src={displaySrc} alt={imageUrl}
                onError={() => setImgError(true)}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}/>
            ) : (
              <>
                <span style={{ display: 'inline-flex', opacity: 0.5 }}><NI.Camera/></span>
                <span style={{ fontSize: 12 }}>
                  {hasImage ? 'Preview unavailable — click to change' : 'Click to pick from catalogue'}
                </span>
              </>
            )}
          </div>
        </MDialog>
      )}
      <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)}
        onSelect={img => { setFreshPreviewUrl(img.previewUrl); setImgError(false); setThumbError(false); onChange(img); setOpen(false); }}/>
    </React.Fragment>
  );
}

export function fileToObjectURL(file: File | null): string | null {
  if (!file) return null;
  return URL.createObjectURL(file);
}

export function ImageDropPicker({ value, onChange, height = 90, label = 'Drop image or click to pick from catalogue' }: {
  value: string | null;
  onChange: (url: string | null) => void;
  height?: number;
  label?: string;
}): JSX.Element {
  const [drag, setDrag] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const ownBlobRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    return () => {
      if (ownBlobRef.current) {
        URL.revokeObjectURL(ownBlobRef.current);
        ownBlobRef.current = null;
      }
    };
  }, []);
  const handleDroppedFile = (file: File | undefined) => {
    if (!file) return;
    if (ownBlobRef.current) URL.revokeObjectURL(ownBlobRef.current);
    const url = URL.createObjectURL(file);
    ownBlobRef.current = url;
    onChange(url);
  };
  return (
    <React.Fragment>
    <div
      id="naomi-preview-section-image-drop-picker"
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); handleDroppedFile(e.dataTransfer.files?.[0]); }}
      onPaste={e => {
        const item = Array.from(e.clipboardData?.items ?? []).find(i => i.type.startsWith('image/'));
        if (item) handleDroppedFile(item.getAsFile() ?? undefined);
      }}
      onClick={() => setPickerOpen(true)}
      style={{ height, borderRadius: 8, cursor: 'pointer', position: 'relative',
        border: `2px dashed ${drag ? NT.primary : NT.line2}`,
        background: value ? `url("${value}") center/cover no-repeat ${NT.surfaceAlt}` : (drag ? NT.primarySoft : NT.surfaceAlt),
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: NT.ink3, fontSize: 12, transition: 'all .15s', overflow: 'hidden' }}>
      {!value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, textAlign: 'center' }}>
          <span style={{ color: drag ? NT.primary : NT.ink4 }}><NI.Camera/></span>
          <span>{label}</span>
        </div>
      )}
      {value && (
        <button id="naomi-preview-iconbutton-drop-image-remove" onClick={e => { e.stopPropagation(); onChange(null); }}
          title="Remove image"
          style={{ position: 'absolute', top: 6, right: 6, width: 22, height: 22,
            border: 'none', borderRadius: 4, background: 'rgba(0,0,0,.55)', color: '#fff',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <NI.Close/>
        </button>
      )}
    </div>
    <CataloguePicker open={pickerOpen} onClose={() => setPickerOpen(false)}
      onSelect={img => { onChange(img.previewUrl || null); setPickerOpen(false); }}/>
    </React.Fragment>
  );
}

export function RenderValidationError({ value, validations, dataType }: {
  value: any;
  validations: any;
  dataType: string;
}): JSX.Element | null {
  const err = evaluateValidation(value, validations, dataType);
  if (!err) return null;
  return (
    <div className="nm-validation-error" style={{ marginTop: 4, fontSize: 11.5, color: NT.danger, display: 'flex',
      alignItems: 'center', gap: 4, fontWeight: 500 }}>
      <span style={{ width: 14, height: 14, borderRadius: 7, background: NT.danger, color: '#fff',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>!</span>
      {err}
    </div>
  );
}

export function IconChoicePreview({ item: _item, codeList, value, set }: any): JSX.Element {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
      {(codeList?.CodeListItem || []).map(ci => {
        const sel = value === ci.CodedValue;
        return (
          <button id={`naomi-preview-button-choice-option-${_item.OID}-${ci.CodedValue}`} key={ci.CodedValue} onClick={() => set(ci.CodedValue)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              border: `1.5px solid ${sel ? NT.primary : NT.line2}`,
              background: sel ? NT.primarySoft : NT.surface, borderRadius: 6, cursor: 'pointer',
              fontFamily: 'inherit', textAlign: 'left' }}>
            {ci._previewUrl ? (
              <img src={ci._previewUrl} alt="" style={{ width: 32, height: 32, borderRadius: 4,
                objectFit: 'cover', flexShrink: 0, background: NT.surfaceAlt }}/>
            ) : (
              <span style={{ width: 32, height: 32, borderRadius: 16, background: sel ? NT.primary : NT.surfaceAlt,
                color: sel ? '#fff' : NT.ink3, display: 'inline-flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                {ci.Decode.TranslatedText.text.slice(0, 1)}
              </span>
            )}
            <span style={{ flex: 1, fontSize: 13, fontWeight: sel ? 600 : 500,
              color: sel ? NT.primary2 : NT.ink2 }}>
              {ci.Decode.TranslatedText.text}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function repeatCountFromAnswers(group: any, answers: Record<string, any>): number | null {
  if (!group?.RepeatFromOID) return null;
  const v = answers[group.RepeatFromOID];
  return Math.max(0, Math.min(20, parseInt(v) || 0));
}

registerLivePreviewExtra('singleselect', () => null);

registerNaomiPlugins({
  ensureChoiceList,
  evaluateValidation,
  renderGroupHeaderBanner,
  GroupHeaderImageDialog,
  ChoiceOptionImageInput,
  ImageDropPicker,
  RenderValidationError,
  IconChoicePreview,
  fileToObjectURL,
  repeatCountFromAnswers,
});
